import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { format } from "date-fns";

interface MeterValueItem {
  dtutc: string;
  dtlt: string;
  date: string;
  hour: number;
  qhour?: number;
  ean: string;
  mv: string;
  pl: string;
  price: string;
  direction?: string;
  belpex?: string;
  max_mv?: string | number;
  max_mv_dtlt?: string;
}

interface BelpexItem {
  dtlt: string;
  price: number;
}

interface StatsData {
  title: string;
  value: string;
  unit: string;
}

interface ExportOptions {
  data: MeterValueItem[];
  stats: StatsData[];
  belpexArray: BelpexItem[];
  ean: string;
  serviceId: string;
  type: string;
  dateRange: { startDate: Date; endDate: Date };
}

type DataGranularity = "quarter-hourly" | "hourly" | "monthly";

function detectGranularity(data: MeterValueItem[], dateRange: { startDate: Date; endDate: Date }): DataGranularity {
  if (data.length > 0 && data[0].qhour !== undefined) return "quarter-hourly";
  const diffDays = (dateRange.endDate.getTime() - dateRange.startDate.getTime()) / (1000 * 60 * 60 * 24);
  if (diffDays <= 7) return "quarter-hourly";
  if (diffDays <= 14) return "hourly";
  return "monthly";
}

function formatDateRange(startDate: Date, endDate: Date): string {
  return `${format(startDate, "yyyy-MM-dd")}_to_${format(endDate, "yyyy-MM-dd")}`;
}

function buildFileName(opts: ExportOptions, extension: string): string {
  const dateStr = formatDateRange(opts.dateRange.startDate, opts.dateRange.endDate);
  return `service-dashboard_${opts.serviceId}_${dateStr}.${extension}`;
}

function buildBelpexLookup(belpexArray: BelpexItem[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const item of belpexArray) {
    map.set(item.dtlt, item.price);
  }
  return map;
}

function quoteCSV(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

function padRow(row: string[], targetLength: number): string[] {
  while (row.length < targetLength) {
    row.push("");
  }
  return row;
}

interface TableConfig {
  headers: string[];
  rows: string[][];
}

function buildTable(data: MeterValueItem[], belpexArray: BelpexItem[], granularity: DataGranularity, type: string): TableConfig {
  const belpexLookup = buildBelpexLookup(belpexArray);
  const isQuarterHourly = granularity === "quarter-hourly";

  const headers = isQuarterHourly
    ? ["Date/Time", "Hour", "Quarter", "Energy (kWh)", "Amount (EUR)", "Belpex (EUR/MWh)", "Power (kW)", "Direction"]
    : granularity === "hourly"
      ? ["Date/Time", "Hour", "Energy (kWh)", "Amount (EUR)", "Belpex (EUR/MWh)", "Max Power (kW)", "Direction"]
      : ["Date", "Energy (kWh)", "Amount (EUR)", "Avg Belpex (EUR/MWh)", "Max Power (kW)", "Direction"];

  const rows = data.map((item) => {
    const mv = parseFloat(String(item.mv) || "0") || 0;
    const pl = parseFloat(String(item.pl) || "0") || 0;

    let belpexPrice = 0;
    if (isQuarterHourly) {
      belpexPrice = parseFloat(String(item.belpex) || "0") || 0;
    } else {
      belpexPrice = belpexLookup.get(item.dtlt) ?? 0;
    }

    let maxMv = 0;
    if (item.max_mv !== undefined && item.max_mv !== null && item.max_mv !== "") {
      maxMv = typeof item.max_mv === "number" ? item.max_mv : parseFloat(String(item.max_mv)) || 0;
    } else if (isQuarterHourly) {
      maxMv = mv * 4;
    } else {
      maxMv = mv;
    }

    const direction = item.direction || type;
    const dateStr = item.dtlt || item.date || "";

    if (isQuarterHourly) {
      return [
        dateStr,
        String(item.hour ?? ""),
        String(item.qhour ?? ""),
        mv.toFixed(4),
        pl.toFixed(4),
        belpexPrice.toFixed(2),
        maxMv.toFixed(2),
        direction,
      ];
    } else if (granularity === "hourly") {
      return [
        dateStr,
        String(item.hour ?? ""),
        mv.toFixed(4),
        pl.toFixed(4),
        belpexPrice.toFixed(2),
        maxMv.toFixed(2),
        direction,
      ];
    } else {
      return [
        dateStr,
        mv.toFixed(4),
        pl.toFixed(4),
        belpexPrice.toFixed(2),
        maxMv.toFixed(2),
        direction,
      ];
    }
  });

  return { headers, rows };
}

export function exportToCSV(opts: ExportOptions): void {
  const { stats } = opts;
  const granularity = detectGranularity(opts.data, opts.dateRange);
  const { headers, rows } = buildTable(opts.data, opts.belpexArray, granularity, opts.type);
  const colCount = headers.length;
  const period = `${format(opts.dateRange.startDate, "dd MMM yyyy")} - ${format(opts.dateRange.endDate, "dd MMM yyyy")}`;

  const metaRows: string[][] = [
    padRow(["Service Dashboard Export"], colCount),
    padRow(["EAN", opts.ean], colCount),
    padRow(["Service", opts.serviceId], colCount),
    padRow(["Type", opts.type], colCount),
    padRow(["Period", period], colCount),
    padRow([], colCount),
    padRow(["Summary"], colCount),
    ...stats.map((s) => padRow([s.title, `${s.value} ${s.unit}`], colCount)),
    padRow([], colCount),
  ];

  const csvLines = [
    ...metaRows.map((row) => row.map(quoteCSV).join(",")),
    headers.map(quoteCSV).join(","),
    ...rows.map((row) => row.map(quoteCSV).join(",")),
  ];

  const csvContent = csvLines.join("\n");
  const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
  downloadBlob(blob, buildFileName(opts, "csv"));
}

export function exportToPDF(opts: ExportOptions): void {
  const { stats } = opts;
  const granularity = detectGranularity(opts.data, opts.dateRange);
  const { headers, rows } = buildTable(opts.data, opts.belpexArray, granularity, opts.type);
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFontSize(18);
  doc.setTextColor(44, 62, 80);
  doc.text("Service Dashboard", 14, 18);

  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(`EAN: ${opts.ean}`, 14, 26);
  doc.text(`Service: ${opts.serviceId}  |  Type: ${opts.type}`, 14, 31);
  doc.text(
    `Period: ${format(opts.dateRange.startDate, "dd MMM yyyy")} - ${format(opts.dateRange.endDate, "dd MMM yyyy")}`,
    14,
    36,
  );

  const statsY = 44;
  const statsBoxWidth = (pageWidth - 28 - 3 * 6) / 4;
  stats.forEach((stat, i) => {
    const x = 14 + i * (statsBoxWidth + 6);
    doc.setFillColor(241, 245, 249);
    doc.roundedRect(x, statsY, statsBoxWidth, 18, 2, 2, "F");
    doc.setFontSize(8);
    doc.setTextColor(100, 130, 160);
    doc.text(stat.title, x + 4, statsY + 6);
    doc.setFontSize(13);
    doc.setTextColor(44, 62, 80);
    doc.text(`${stat.value} ${stat.unit}`, x + 4, statsY + 14);
  });

  autoTable(doc, {
    startY: statsY + 26,
    head: [headers],
    body: rows,
    theme: "grid",
    headStyles: {
      fillColor: [44, 62, 80],
      textColor: [255, 255, 255],
      fontSize: 7,
      fontStyle: "bold",
    },
    bodyStyles: {
      fontSize: 6.5,
      textColor: [50, 50, 50],
    },
    alternateRowStyles: {
      fillColor: [245, 248, 252],
    },
    styles: {
      cellPadding: 2,
      lineColor: [220, 220, 220],
      lineWidth: 0.1,
    },
    margin: { left: 14, right: 14 },
  });

  doc.save(buildFileName(opts, "pdf"));
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
